import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useSysTheme, useDisplayTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import {
  Button,
  Menu,
  CaretDown as CaretDownIcon
  // @ts-ignore
} from '@netbasequid/core'
import { NBColor } from 'enums'
import { TimelineAnnotation } from 'schemas/TimelineAnnotationSchema'
import SpinningQ from 'components/share/SpinningQ'
import useToast from 'hooks/useToast'
import { toExternal } from 'vo/util'
import AnnotationIcon from './AnnotationIcon'
import { AnnotationCardWrapper as AnnotationDisplayCardWrapper } from './Card'

interface MenuItemClickEvent {
  target: {
    value: string
  }
}

const AnnotationCardWrapper = styled(AnnotationDisplayCardWrapper)<{ isSaving?: boolean }>`
  gap: 4px;
  opacity: ${({ isSaving }) => (isSaving ? 0.4 : 1)};
  pointer-events: ${({ isSaving }) => (isSaving ? 'none' : 'auto')};

  & > div:last-child {
    flex: 1;
  }
`

const AnnotationContent = styled.div<{ $theme: SystemTheme }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  & > textarea {
    width: 100%;
    box-sizing: border-box;
    border: ${({ $theme }) => $theme.interaction.border.default};
    border-radius: 2px;
    outline: none;
    resize: none;
    padding: 6px 6px 6px 10px;
  }
  & > div {
    margin-top: 8px;
    display: flex;
    gap: 8px;
  }
`

const StyledButton = styled(Button)<{ $theme: SystemTheme }>`
  padding: 1px 0px 1px 4px;
  height: 20px;
  margin-left: -4px;
  & > div:last-child {
    margin-left: 4px;
  }
`

const EditCard = ({
  annotation,
  onCancel,
  onUpdate,
  onAdd,
  isSaving
}: {
  annotation?: TimelineAnnotation
  onCancel?: (annotationId: number) => void
  onUpdate?: (annotation: TimelineAnnotation) => void
  onAdd?: (annotation: Pick<TimelineAnnotation, 'color' | 'description'>) => void
  isSaving?: boolean
}) => {
  const theme = useSysTheme()
  const mode = useDisplayTheme()
  const { t } = useTranslation()

  const buttonRef = useRef(null)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [open, setOpen] = useState(false)
  const [colorValue, setColorValue] = useState(annotation?.color ?? NBColor.GREY)
  const [addToast] = useToast()

  const handleItemClick = (e: MenuItemClickEvent) => {
    setColorValue(e.target.value)
    setOpen(false)
  }

  return (
    <AnnotationCardWrapper $theme={theme} isSaving={isSaving}>
      <div>
        <StyledButton
          mode={mode}
          ref={buttonRef}
          variant="ghost"
          color="secondary"
          endIcon={<CaretDownIcon fontSize={16} />}
          onClick={() => {
            setOpen(true)
          }}>
          <AnnotationIcon color={colorValue} />
        </StyledButton>
        <Menu
          mode={mode}
          open={open}
          anchorEl={buttonRef.current}
          onClose={() => {
            setOpen(false)
          }}>
          <Menu.MenuItem value={NBColor.GREY} onClick={handleItemClick}>
            <AnnotationIcon color={NBColor.GREY} />
          </Menu.MenuItem>
          <Menu.MenuItem value={NBColor.RED} onClick={handleItemClick}>
            <AnnotationIcon color={NBColor.RED} />
          </Menu.MenuItem>
          <Menu.MenuItem value={NBColor.GREEN} onClick={handleItemClick}>
            <AnnotationIcon color={NBColor.GREEN} />
          </Menu.MenuItem>
          <Menu.MenuItem value={NBColor.ORANGE} onClick={handleItemClick}>
            <AnnotationIcon color={NBColor.ORANGE} />
          </Menu.MenuItem>
        </Menu>
      </div>
      <AnnotationContent $theme={theme}>
        <textarea
          name="annotation"
          ref={textAreaRef}
          placeholder={t('annotationPlaceholder')}
          defaultValue={annotation?.description}
          rows={4}
          maxLength={160}
          onBeforeInput={(e) => {
            if (
              (e.target as HTMLTextAreaElement).value.length +
                (((e as unknown) as InputEvent)!.data?.length ?? 0) >=
              160
            ) {
              addToast({
                toastId: 'annotationMaxLength',
                message: (
                  <span>
                    {t('annotationTextMaxLength')}
                    <br />
                    {t('autoTruncatedAnnotation')}
                  </span>
                )
              })
            }
          }}
        />
        <div>
          {annotation ? (
            <>
              <Button mode={mode} color="secondary" onClick={() => onCancel?.(annotation.id!)}>
                {t('cancel')}
              </Button>
              <Button
                mode={mode}
                color="primary"
                startIcon={isSaving ? <SpinningQ /> : undefined}
                onClick={() =>
                  onUpdate?.(
                    toExternal({
                      ...annotation!,
                      color: colorValue,
                      description: textAreaRef.current?.value
                    })
                  )
                }>
                {isSaving ? `${t('savingThreeDot')}` : t('save')}
              </Button>
            </>
          ) : (
            <Button
              mode={mode}
              color={isSaving ? 'primary' : 'secondary'}
              startIcon={isSaving ? <SpinningQ /> : undefined}
              onClick={() =>
                onAdd?.({
                  color: colorValue,
                  description: textAreaRef.current?.value
                })
              }>
              {isSaving ? `${t('savingThreeDot')}` : t('add')}
            </Button>
          )}
        </div>
      </AnnotationContent>
    </AnnotationCardWrapper>
  )
}

export default EditCard
